/**
 * Fallback: CaptchaConfigProvider dışında kullanıldığında tek başına fetch yapar.
 * Normalde tüm uygulama CaptchaConfigProvider içinde olduğu için bu path nadiren çalışır.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

export interface CaptchaConfig {
  provider: 'google_recaptcha_v2' | 'google_recaptcha_v3' | 'google_recaptcha_enterprise' | 'cloudflare_turnstile' | 'honeypot' | null;
  site_key: string | null;
  is_enabled: boolean;
  script_url: string | null;
}

export interface UseCaptchaConfigReturn {
  config: CaptchaConfig | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const CACHE_TTL = 3600000;
let globalConfigCache: CaptchaConfig | null = null;
let globalConfigCacheTime = 0;

export function useCaptchaConfigFallback(enabled: boolean = true): UseCaptchaConfigReturn {
  const [config, setConfig] = useState<CaptchaConfig | null>(globalConfigCache);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchConfig = useCallback(async () => {
    if (!enabled) {
      setConfig(null);
      setIsLoading(false);
      return;
    }
    const now = Date.now();
    if (globalConfigCache && now - globalConfigCacheTime < CACHE_TTL) {
      setConfig(globalConfigCache);
      setIsLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/captcha/config', {
        signal: abortRef.current.signal,
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!res.ok) throw new Error(`Captcha config failed: ${res.status}`);
      const data = await res.json();
      const raw = (data.data ?? (data.success && data.data ? data.data : null)) as CaptchaConfig | null;
      const next = raw && typeof raw === 'object'
        ? { provider: raw.provider ?? null, site_key: raw.site_key ?? null, is_enabled: !!raw.is_enabled, script_url: raw.script_url ?? null }
        : { provider: null, site_key: null, is_enabled: false, script_url: null };
      globalConfigCache = next;
      globalConfigCacheTime = Date.now();
      setConfig(next);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Captcha config alınamadı');
      const fallback = { provider: null, site_key: null, is_enabled: false, script_url: null };
      setConfig(fallback);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    fetchConfig();
    return () => { abortRef.current?.abort(); };
  }, [fetchConfig]);

  return { config, isLoading, error, refetch: fetchConfig };
}
